import {
  Alert,
  Button,
  FormControl,
  FormLabel,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WhiteCard from "src/shared/components/WhiteCard";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import { ChangeEvent, FormEvent, useCallback, useMemo, useState } from "react";
import { topLevelDomains } from "src/constants/topLevelDomains";
import {
  useCreateReferralMutation,
  useMyReferralCodeQuery,
} from "src/api/services/referralsApi";
import { useSentryLogger } from "src/hooks/useSentryLogger";

const ReferAFriend = () => {
  const [email, setEmail] = useState("");
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createReferral, { isLoading }] = useCreateReferralMutation();
  const errorLog = useSentryLogger();
  const { data: code } = useMyReferralCodeQuery();

  const handleClose = useCallback(
    (_event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
      if (reason === "clickaway") {
        return;
      }

      setOpen(false);
    },
    []
  );

  const disabledButton = useMemo(
    () =>
      !email.includes("@") ||
      !topLevelDomains.some((tld) => email.toLowerCase().endsWith(tld)),
    [email]
  );

  const handleEmail = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  }, []);

  const handleReferral = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (code?.promo_code == null) {
        errorLog("No referral code found for user attempting to refer");
        return;
      }

      await createReferral({ email, promo_code: code?.promo_code })
        .unwrap()
        .then(() => {
          setEmail("");
          setOpen(true);
        })
        .catch((err) => {
          errorLog("Error creating referral", err);
          setError(
            err.data?.message ||
              err.message ||
              "Something went wrong, please try again"
          );
        });
    },
    [code?.promo_code, createReferral, email, errorLog]
  );

  return (
    <Stack gap={3}>
      <Typography variant="h5" fontWeight={700}>
        Refer a Friend
      </Typography>
      <WhiteCard>
        <CardGiftcardIcon fontSize="large" />
        <Typography variant="h6" fontWeight={600}>
          Earn a $10 bonus for each person you refer to HolaVida & your friend
          also earns $10
        </Typography>
        <Stack gap={2} component="form" onSubmit={handleReferral}>
          <FormControl sx={{ gap: 2, maxWidth: 400 }}>
            <FormLabel sx={{ fontWeight: 700 }}>
              Add your friend’s email
            </FormLabel>
            <TextField
              required
              placeholder="Email"
              onChange={handleEmail}
              value={email}
            />
          </FormControl>
          {error != null && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
          <Button
            fullWidth
            disabled={disabledButton}
            loading={isLoading}
            type="submit"
          >
            Refer my friend
          </Button>
        </Stack>
      </WhiteCard>
      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} sx={{ width: "100%" }}>
          Referral sent successfully!
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default ReferAFriend;
